import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { displayBonusLabel, getBonusesForDate, getBonusLogs, getBonusTypes, getBonusZones } from '../api/bonuses';
import { DailyBonusesGrid } from '../components/resourceHunter/DailyBonusesGrid';
import { ResourceBonusGridControls } from '../components/resourceHunter/ResourceBonusGridControls';
import { ResourceHunterTabs } from '../components/resourceHunter/ResourceHunterTabs';
import {
  DEFAULT_RESOURCE_BONUS_FILTERS,
  DEFAULT_RESOURCE_BONUS_SORT,
  filterAndSortBonusRows,
  getResourceBonusFilterOptions,
  hasActiveResourceBonusFilters
} from '../components/resourceHunter/resourceHunterUtils';
import { getActiveServerSlug } from '../utils/siteContext';

function todayString() {
  return new Date().toISOString().slice(0, 10);
}

export function BonusesHistoryPage() {
  const serverSlug = getActiveServerSlug();
  const serverDisplayName = serverSlug.charAt(0).toUpperCase() + serverSlug.slice(1);
  const [date, setDate] = useState(todayString());
  const [gridFilters, setGridFilters] = useState(DEFAULT_RESOURCE_BONUS_FILTERS);
  const [gridSort, setGridSort] = useState(DEFAULT_RESOURCE_BONUS_SORT);

  const typesQuery = useQuery({
    queryKey: ['bonus-types', serverSlug],
    queryFn: () => getBonusTypes(serverSlug)
  });

  const zonesQuery = useQuery({
    queryKey: ['bonus-zones', serverSlug],
    queryFn: () => getBonusZones(serverSlug)
  });

  const bonusesQuery = useQuery({
    queryKey: ['bonus-history-date', serverSlug, date],
    queryFn: () => getBonusesForDate(serverSlug, date)
  });

  const logsQuery = useQuery({
    queryKey: ['bonus-history-logs', serverSlug, date],
    queryFn: () => getBonusLogs(serverSlug, date)
  });

  const rows = bonusesQuery.data ?? [];
  const filterOptions = useMemo(() => getResourceBonusFilterOptions(rows), [rows]);
  const visibleRows = useMemo(() => filterAndSortBonusRows(rows, gridFilters, gridSort), [gridFilters, gridSort, rows]);
  const hasFilters = hasActiveResourceBonusFilters(gridFilters);

  return (
    <main className="main-content">
      <section className="page-hero">
        <div className="content-wrapper py-4">
          <h1 className="display-6 fw-semibold mb-3">
            Resource Hunter Bonuses
            <span className="page-hero__server d-block">{serverDisplayName}</span>
          </h1>
          <p className="fs-3 mb-0">Bonus History & Action Log</p>
        </div>
      </section>

      <ResourceHunterTabs activeTab="history" />

      <div className="content-wrapper py-4">
        <section className="report-panel mb-4">
          <label className="form-label" htmlFor="bonus-history-date">
            Date
          </label>
          <input
            id="bonus-history-date"
            className="form-control"
            style={{ maxWidth: 260 }}
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
          />
        </section>

        <section className="report-panel mb-4">
          <ResourceBonusGridControls
            filters={gridFilters}
            sort={gridSort}
            expansionOptions={filterOptions.expansions}
            zoneTypeOptions={filterOptions.zoneTypes}
            visibleCount={visibleRows.length}
            totalCount={rows.length}
            hasActiveFilters={hasFilters}
            onFiltersChange={setGridFilters}
            onSortChange={setGridSort}
            onResetFilters={() => setGridFilters(DEFAULT_RESOURCE_BONUS_FILTERS)}
          />

          {bonusesQuery.isLoading || typesQuery.isLoading || zonesQuery.isLoading ? <p>Loading bonuses...</p> : null}
          {bonusesQuery.isError ? <p className="text-danger">{bonusesQuery.error.message}</p> : null}

          <DailyBonusesGrid
            rows={visibleRows}
            assignmentRows={rows}
            zones={zonesQuery.data ?? []}
            bonusTypes={typesQuery.data ?? []}
            canEdit={false}
            readOnly
          />
        </section>

        <section className="report-panel p-3 p-md-4">
          <h2 className="h4 mb-3">Change log</h2>
          {logsQuery.isLoading ? <p>Loading...</p> : null}
          {logsQuery.isError ? <p className="text-danger">{logsQuery.error.message}</p> : null}
          <div className="d-grid gap-2">
            {(logsQuery.data ?? []).map((log) => (
              <article className="bonus-zone-card" key={log.id}>
                <div className="fw-semibold">{log.actionType}</div>
                <div className="small text-body-secondary">
                  {[log.zone?.name, displayBonusLabel(log.bonusType), log.actor?.username].filter(Boolean).join(' · ')}
                </div>
              </article>
            ))}
          </div>
        </section>
      </div>
    </main>
  );
}
